import React from 'react'
import { Link } from 'react-router-dom'
import { useI18nContext } from '../hooks/I18nContext'

const Home: React.FC = () => {
  const { t } = useI18nContext()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-pink-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="min-h-screen flex items-center justify-center px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-6xl md:text-8xl font-bold text-gray-800 mb-6">
                {t('hero.title')}
              </h1>
              <p className="text-2xl md:text-3xl text-gray-600 mb-8">
                {t('hero.subtitle')}
              </p>
              <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
                {t('hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/start"
                  className="inline-block text-white px-8 py-4 rounded-full transition-all duration-300 text-lg font-semibold hover:opacity-90"
                  style={{backgroundColor: '#76C3D7'}}
                >
                  {t('hero.cta')} →
                </Link>
                <Link
                  to="/buy"
                  className="inline-block bg-white hover:bg-gray-50 px-8 py-4 rounded-full transition-all duration-300 text-lg font-semibold border-2"
                  style={{color: '#76C3D7', borderColor: '#76C3D7'}}
                >
                  {t('cta.button')}
                </Link>
              </div>
            </div>
          </section>

          {/* Product Features */}
          <section className="py-20 px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                {t('features.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: '#76C3D7'}}>
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{t('features.lily.title')}</h3>
                  <p className="text-gray-600">
                    {t('features.lily.description')}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: '#9acd97'}}>
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{t('features.safety.title')}</h3>
                  <p className="text-gray-600">
                    {t('features.safety.description')}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: '#76C3D7'}}>
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{t('features.fun.title')}</h3>
                  <p className="text-gray-600">
                    {t('features.fun.description')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-20 px-8 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-800 text-center mb-12">
                {t('testimonials.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-green-500 rounded-full mr-4 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">S</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{t('testimonials.sarah.name')}</h3>
                      <p className="text-gray-600">{t('testimonials.sarah.location')}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic mb-4">
                    "{t('testimonials.sarah.quote')}"
                  </p>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-8">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-blue-500 rounded-full mr-4 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">M</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{t('testimonials.michael.name')}</h3>
                      <p className="text-gray-600">{t('testimonials.michael.location')}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic mb-4">
                    "{t('testimonials.michael.quote')}"
                  </p>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                {t('cta.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/start"
                  className="inline-block text-white px-8 py-4 rounded-full transition-all duration-300 text-lg font-semibold hover:opacity-90"
                  style={{backgroundColor: '#76C3D7'}}
                >
                  {t('hero.cta')}
                </Link>
                <Link
                  to="/learning"
                  className="inline-block bg-white hover:bg-gray-50 px-8 py-4 rounded-full transition-all duration-300 text-lg font-semibold border-2"
                  style={{color: '#76C3D7', borderColor: '#76C3D7'}}
                >
                  {t('cta.learnMore')}
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Home
